import React, { useState, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { capitalize } from "lodash";

import {
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  FormControl,
  makeStyles,
  LinearProgress,
  FormControlLabel,
  Checkbox,
  useTheme,
} from "@material-ui/core";

import { PermissionedDisplay } from "../../../components/Layout";
import { RiderContext } from "../../../context";
import { KeyValueDisplay } from "../../../components/Typography";
import { FilledTextField } from "../../../components/FormElements";
import { spTheme } from "../../../spTheme";
import { StringUtils } from "../../../util";
import formatNumber from "../../../util/strings/formatNumber";
import passTransactionParser from "../../../util/parsers/passTransactions";
import Permissions from "../../../permissions";
import { useTestIds } from "../../../hooks/useTestIds";

const useStyles = makeStyles(() => ({
  Checkbox: {
    color: "red",
  },
  FormControlLabel: {
    color: "darkgray",
  },
}));

const ManagePassModal = () => {
  const {
    activePass,
    deactivatePass,
    fetchActivePass,
    cashOutPassTransaction,
    passTransactions,
    fetchPassTransactions,
  } = useContext(RiderContext);

  const testIds = useTestIds("rider-profile-manage-pass-modal");

  const { t } = useTranslation();
  const theme = useTheme();
  const classes = useStyles(theme);
  const [isOpen, setIsOpen] = useState(true);
  const { riderId } = useParams();
  const history = useHistory();
  const [submitting, setSubmitting] = useState(false);
  const [checkDeactivatePass, setCheckDeactivatePass] = useState(false);
  const [amountError, setAmountError] = useState(false);
  const [helperText, setHelperText] = useState(null);
  const [amount, setAmount] = useState("");
  const [managePassDetail, setManagePassDetail] = useState(null);

  const handleCancel = (e = null) => {
    if (e) e.stopPropagation();
    setCheckDeactivatePass(false);
    setIsOpen(false);
    history.goBack();
  };

  useEffect(() => {
    if (!activePass) {
      fetchActivePass(riderId);
    }
  }, [activePass, riderId]);

  useEffect(() => {
    if (activePass) {
      fetchPassTransactions(riderId, activePass.id);
    }
  }, [activePass, riderId]);

  useEffect(() => {
    if (passTransactions.length > 0) {
      setManagePassDetail(passTransactionParser(passTransactions[0], activePass));
    }
  }, [passTransactions]);

  useEffect(() => {
    const num = Number(amount);
    if (amount && Number.isNaN(num)) {
      setHelperText("Not a valid number");
      setAmountError(true);
    } else if (num && num <= 0) {
      setHelperText("Must be > 0.00");
      setAmountError(true);
    } else if (num && num > managePassDetail?.availableForCashout) {
      setHelperText("Must be < available for cash out");
      setAmountError(true);
    } else {
      setHelperText(null);
      setAmountError(false);
    }
  }, [amount]);

  const confirmDeactivatePass = async () => {
    try {
      await deactivatePass(activePass?.id);
    } catch (e) {
      console.error(e);
    } finally {
      setCheckDeactivatePass(false);
    }
  };

  const deactivatePassHandler = (event) => {
    setCheckDeactivatePass(event.target.checked);
  };

  const handleSubmit = () => {
    setSubmitting(true);
    if (checkDeactivatePass) {
      confirmDeactivatePass();
    } else {
      if (amountError) {
        setSubmitting(false);
        return;
      }
      cashOutPassTransaction(Number(amount), managePassDetail?.currentTransactionId, riderId);
    }
    handleCancel();
  };

  const remainingTrip = activePass?.pass_offer?.pass_offer_type?.max_rides
    ? `${activePass.rides_remaining}/${activePass.pass_offer?.pass_offer_type?.max_rides}`
    : t("Riders.Wallet.Unlimited");

  const purchaseDate = StringUtils.convertTz(
    activePass?.purchase_date,
    "UTC",
    "UTC",
    "DATETIME_MED",
  );
  const expirationDate = StringUtils.convertTz(
    activePass?.expiration_date,
    "UTC",
    "UTC",
    "DATETIME_MED",
  );

  return (
    <Dialog data-testid={testIds.wrapper} open={isOpen} onClose={handleCancel} maxWidth="sm">
      <DialogTitle data-testid={testIds.title}>{t("Riders.Wallet.ManagePassTitle")}</DialogTitle>
      <DialogContent data-testid={testIds.content}>
        <Grid container spacing={2} justify="space-evenly" direction="column">
          <PermissionedDisplay
            permission={[
              Permissions.feature.passes,
              Permissions.readPasses,
              Permissions.writePasses,
              Permissions.readPassOffers,
              Permissions.readPassOfferTypes,
              Permissions.cashoutPasses,
            ]}
          >
            <Grid item xs={12}>
              <KeyValueDisplay
                minWidth="500px"
                label={t("Riders.ManagePass.PassType")}
                text={capitalize(activePass?.pass_offer.pass_offer_type.offer_type)}
                justify="space-between"
                tooltip={`${t("Riders.ManagePass.PurchaseDate")}: ${purchaseDate},
              ${t("Riders.ManagePass.ExpirationDate")}: ${expirationDate}`}
                labelMinWidth="25%"
                testIds={{
                  label: testIds.passTypeLabel,
                  text: testIds.passTypeValue,
                  tooltipIcon: testIds.passTypeTooltipIcon,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12}>
              <KeyValueDisplay
                label={t("Riders.ManagePass.AvailableForCashOut")}
                text={
                  managePassDetail && managePassDetail.status !== "failed"
                    ? formatNumber(
                        managePassDetail?.availableForCashout,
                        managePassDetail?.currency,
                      )
                    : "--"
                }
                justify="space-between"
                testIds={{
                  label: testIds.availableForCashOutLabel,
                  text: testIds.availableForCashOutValue,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <KeyValueDisplay
                label={t("Riders.ManagePass.PreviouslyCashedOut")}
                text={
                  managePassDetail && managePassDetail.status !== "failed"
                    ? formatNumber(
                        managePassDetail?.previouslyCashedOut,
                        managePassDetail?.currency,
                      )
                    : "--"
                }
                justify="space-between"
                testIds={{
                  label: testIds.previouslyCashedOutLabel,
                  text: testIds.previouslyCashedOutValue,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12}>
              <KeyValueDisplay
                label={`${t("Riders.ManagePass.RemainingPassValue")} ( ${
                  managePassDetail?.remainingPassDuration
                } )`}
                text={formatNumber(
                  managePassDetail?.remainingPassValue,
                  managePassDetail?.currency,
                )}
                justify="space-between"
                tooltip={`${t("Riders.ManagePass.Value/Day")}: ${formatNumber(
                  managePassDetail?.valuePerDay,
                  managePassDetail?.currency,
                )} ${t("Riders.ManagePass.Value/Hour")}: ${formatNumber(
                  managePassDetail?.valuePerHour,
                  managePassDetail?.currency,
                )}`}
                labelMinWidth="25%"
                testIds={{
                  label: testIds.remainingPassLabel,
                  text: testIds.remainingPassValue,
                  tooltipIcon: testIds.remainingPassTooltipIcon,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <KeyValueDisplay
                label={`${t("Riders.ManagePass.RemainingTripValue")} (${remainingTrip})`}
                text={formatNumber(
                  managePassDetail?.remainingTripValue,
                  managePassDetail?.currency,
                )}
                justify="space-between"
                tooltip={`${t("Riders.ManagePass.Value/Trip")}: ${formatNumber(
                  managePassDetail?.valuePerTrip,
                  managePassDetail?.currency,
                )}`}
                labelMinWidth="25%"
                testIds={{
                  label: testIds.remainingTripLabel,
                  text: testIds.remainingTripValue,
                  tooltipIcon: testIds.remainingTripTooltipIcon,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12}>
              <FilledTextField
                value={amount}
                mask={managePassDetail?.currency}
                onChange={(e) => setAmount(e.target.value)}
                error={amountError}
                helperText={helperText}
                label={t("Riders.ManagePass.AmountToCashOut")}
                underlineColor={spTheme.palette.primary.main}
                testIds={{
                  input: testIds.amountToCashOutInput,
                }}
              />
            </Grid>
          </PermissionedDisplay>
          <Grid item xs={12}>
            <PermissionedDisplay
              permission={[
                Permissions.feature.passes,
                Permissions.readPasses,
                Permissions.writePasses,
                Permissions.readPassOffers,
                Permissions.readPassOfferTypes,
              ]}
            >
              <FormControl variant="outlined">
                <FormControlLabel
                  className={classes.FormControlLabel}
                  control={
                    <Checkbox
                      className={classes.Checkbox}
                      checked={checkDeactivatePass}
                      onChange={deactivatePassHandler}
                      data-testid={testIds.deactivatePassCheckboxInput}
                    />
                  }
                  label={t("Riders.Wallet.DeactivatePassLabel")}
                  data-testid={testIds.deactivatePassCheckboxLabel}
                />
              </FormControl>
            </PermissionedDisplay>
          </Grid>
        </Grid>
        {submitting && <LinearProgress color="secondary" />}
      </DialogContent>

      <DialogActions>
        <Button data-testid={testIds.cancelButton} onClick={handleCancel}>
          {t("Riders.Wallet.Cancel")}
        </Button>
        <Button
          data-testid={testIds.saveButton}
          disabled={!checkDeactivatePass && !amount}
          onClick={handleSubmit}
        >
          {t("Riders.Wallet.Save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManagePassModal;
